import * as dataAccess from "@/modules/dataAccess";
import * as chainSession from "@/modules/ChainSession";
import {
  buildPublisherChainApiUrl,
  buildChainRequestBody,
} from "@/modules/experienceApiUrl";
import { getViewerDeviceKey } from "@/modules/viewerDeviceKey";
import { readMcpScope } from "@/modules/mcpScope";
import {
  resolveViewerSurface,
  VIEWER_SURFACE_MCP_APP,
} from "@/modules/viewerSurface";
import type { ChainRecommendation, ExperienceId } from "@/types/experience";
import { dispatchUnhandled } from "@/stores/createStore";
import { chainStore } from "@/stores/chain/store";
import type {
  ChainFailedPayload,
  ChainLoadedPayload,
  ChainLoadingPayload,
} from "@/stores/chain/types";

// The request omits `limit`: the chain config behind /chain sets the count.

// The id already prefetched (or in flight). Repeated media-ready / loadstart
// events for the same id are no-ops, and a failed fetch is not retried (the
// /chain endpoint is rate limited). Module-level: one active experience per
// page, like the store and ChainSession.
let prefetchedForId: ExperienceId | null = null;

// Increases on every chainLoading so the store can tell two in-flight requests
// for the same id apart (see ChainState.token).
let chainRequestToken = 0;

// preview-server.js inlines the first experience's chain as
// window.__PIXMOTO_CHAIN__. Returns it when it is for `idexperience` and this
// load's surface, and consumes it: a later visit to the same id fetches again.
function consumeInlinedChain(
  idexperience: ExperienceId,
): ChainRecommendation[] | null {
  if (typeof window === "undefined" || !window.__PIXMOTO_CHAIN__) {
    return null;
  }
  // The inlined request carries no publication_scope, so on a custom-MCP page
  // (?pix_scope=...) it can hold experiences outside the MCP's publishers.
  if (readMcpScope().length) {
    window.__PIXMOTO_CHAIN__ = null;
    return null;
  }
  // The server cannot tell play.pixmoto.com from a publisher embed, but it
  // stamps ?pix_surface=mcp-app on the payload. Trust the inline only when it
  // was fetched for the surface this load resolved to.
  const surface = resolveViewerSurface();
  const inlined = window.__PIXMOTO_CHAIN__;
  const inlinedSurface = inlined?.surface;
  const inlineMatchesSurface =
    surface === VIEWER_SURFACE_MCP_APP
      ? inlinedSurface === surface
      : !inlinedSurface || inlinedSurface === surface;
  if (!inlineMatchesSurface) {
    window.__PIXMOTO_CHAIN__ = null;
    return null;
  }
  if (
    !inlined ||
    !Array.isArray(inlined.chain) ||
    String(inlined.forExperienceId) !== String(idexperience)
  ) {
    return null;
  }
  window.__PIXMOTO_CHAIN__ = null;
  return inlined.chain as ChainRecommendation[];
}

/**
 * Fetches the chain for the current experience and dispatches loading, then
 * loaded or failed. Its own work never throws, so it cannot break the
 * media-ready handler that calls it. It ends with a dispatch that no store
 * handles, which throws when prefetchChain runs inside another dispatch.
 */
export function prefetchChain(idexperience: unknown): false {
  startPrefetch(idexperience);
  dispatchUnhandled();
  return false;
}

function startPrefetch(id: unknown) {
  try {
    if (id == null || id === prefetchedForId) {
      return;
    }
    // Callers pass the id as they received it, a number or a string.
    const idexperience = id as ExperienceId;
    // Inlined chain: chainLoading first so forExperienceId matches before
    // chainLoaded (which drops a mismatched result).
    const inlinedChain = consumeInlinedChain(idexperience);
    if (inlinedChain) {
      prefetchedForId = idexperience;
      const inlinedPayload: ChainLoadingPayload = {
        forExperienceId: idexperience,
      };
      chainLoading(inlinedPayload);
      chainLoaded({
        chain: inlinedChain,
        forExperienceId: idexperience,
        token: inlinedPayload.token,
      });
      return;
    }
    const url = buildPublisherChainApiUrl();
    if (!url) {
      console.warn(
        "[chain] prefetch skipped: no API base URL " +
          "(window.PIXMOTO_API_BASE_URL unset)",
      );
      return;
    }
    // A returning viewer's Mixpanel $device_id is already in localStorage, so
    // the key exists before Mixpanel initializes; a first-time viewer gets a
    // keyless request, which returns the same chain. Latch now either way.
    const deviceKey = getViewerDeviceKey();
    prefetchedForId = idexperience;
    const body = buildChainRequestBody(
      idexperience,
      chainSession.toChainParams(),
      null,
      deviceKey,
    );
    const loadingPayload: ChainLoadingPayload = {
      forExperienceId: idexperience,
    };
    chainLoading(loadingPayload);
    const token = loadingPayload.token;
    dataAccess
      .getChain(url, body)
      .then(chain => {
        chainLoaded({
          chain: chain,
          forExperienceId: idexperience,
          token: token,
        });
      })
      .catch(() => {
        chainFailed({ forExperienceId: idexperience, token: token });
      });
  } catch (e) {
    console.warn("[chain] prefetch error (ignored):", e);
  }
}

/** Stamps a new token on `payload` in place; the caller reads it back. */
export function chainLoading(payload: ChainLoadingPayload) {
  payload.token = ++chainRequestToken;
  return chainStore.dispatch(payload, (state, p) => {
    state.status = "loading";
    state.chain = [];
    state.forExperienceId = p.forExperienceId;
    state.token = p.token ?? null;
  });
}

export function chainLoaded(payload: ChainLoadedPayload) {
  return chainStore.dispatch(payload, (state, p) => {
    // A result for another experience, or an older request for this one, is stale.
    if (
      p.forExperienceId !== state.forExperienceId ||
      p.token !== state.token
    ) {
      return;
    }
    state.status = "ready";
    state.chain = p.chain || [];
  });
}

export function chainFailed(payload: ChainFailedPayload) {
  return chainStore.dispatch(payload, (state, p) => {
    // A late stale failure must not wipe a chain a newer request applied.
    if (
      p.forExperienceId !== state.forExperienceId ||
      p.token !== state.token
    ) {
      return;
    }
    state.status = "error";
    state.chain = [];
  });
}
